import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteFacilities() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/facilities",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Facilities");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Facilities" />
            <TabHeader
                className="px-3 pt-3"
                title="Facilities"
                highlights={["Add, edit, and delete rows"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: {} })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/facilities/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Facilities Deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = row.is_active;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.id}. {row.title || "Untitled"}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_active: true, is_published: true, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        requestAPI({
            requestPath: "website/facilities",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Facilities Saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add" : "Edit"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                    <FloatLabel className="mt-5">
                        <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="title">Title</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                        <label htmlFor="description">Description</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="icon" className="w-full" value={record?.icon ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, icon: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="icon">Icon</label>
                    </FloatLabel>
                    <WebsiteMediaInput label="Facility photo" field="image" hint="Required size: 1280 ? 720 px (16:9). A larger photo is scaled to fit the Facilities frame. The home page fits the same whole photo inside a small square." record={record} setRecord={setRecord} disabled={saving} />
                    <FloatLabel className="mt-5">
                        <InputText id="image_alt" className="w-full" value={record?.image_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, image_alt: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="image_alt">Image Alt</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="category" className="w-full" value={record?.category ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, category: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="category">Category</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="badge" className="w-full" value={record?.badge ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, badge: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="badge">Badge</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="detail_url" className="w-full" value={record?.detail_url ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, detail_url: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="detail_url">Detail Url</label>
                    </FloatLabel>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="featured" checked={!!record?.featured} onChange={(event) => setRecord((prev) => ({ ...prev, featured: event.checked }))} disabled={saving} />
                        <label htmlFor="featured" className={TEXT_SMALL}>Featured</label>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="special" checked={!!record?.special} onChange={(event) => setRecord((prev) => ({ ...prev, special: event.checked }))} disabled={saving} />
                        <label htmlFor="special" className={TEXT_SMALL}>Special</label>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="is_active" checked={!!record?.is_active} onChange={(event) => setRecord((prev) => ({ ...prev, is_active: event.checked }))} disabled={saving} />
                        <label htmlFor="is_active" className={TEXT_SMALL}>Is Active</label>
                    </div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="points" className="w-full" rows={4} autoResize value={record?.points || ""} onChange={(event) => setRecord((prev) => ({ ...prev, points: event.target.value }))} disabled={saving} />
                        <label htmlFor="points">Points</label>
                    </FloatLabel>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
